"use client";

import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { sanitizeAnalyticsUrl } from "@/lib/analytics/sanitize-url";

/**
 * Google Analytics 4 — chargement direct (sans consent gate).
 *
 * Configuration cross-subdomain : sahha.ma redirige (301) vers admin.sahha.ma
 * et pro.sahha.ma. Sans cookie partagé, GA4 voit chaque sous-domaine comme
 * une session différente et perd la landing page d'origine — c'est la cause
 * des 49 sessions Organic Search avec landing « (not set) » (GA4, 28j mai 2026).
 *
 * Fix : `cookie_domain: '.sahha.ma'` + `linker.domains` pour préserver la
 * session et l'attribution à travers les sous-domaines.
 *
 * Tracking page_view manuel (depuis audit GA4 2026-06-15) :
 * `send_page_view: false` + `<GA4PageTracker>` qui écoute usePathname()/
 * useSearchParams() et envoie le hit AVEC params explicites APRÈS un délai
 * permettant à React App Router de patcher document.title. Sans ça, le hit
 * auto envoyait `document.title` au moment du SEND network (pas au moment
 * de l'event) — race condition quand l'user navigue rapidement (home →
 * connexion) : landing="/" mais page_title="Connexion — Sahha" (capté trop
 * tard). Résultat GA4 : 30 sess landing="/" title="(not set)" + 15 sess
 * landing="(not set)" (page_location absent au send).
 */

const SAHHA_SUBDOMAINS = [
  "sahha.ma",
  "pro.sahha.ma",
  "admin.sahha.ma",
  "app.sahha.ma",
];

export function GoogleAnalytics({ measurementId }: { measurementId: string }) {
  if (!measurementId) return null;

  return (
    <>
      {/* afterInteractive (depuis 2026-05-17, ex-lazyOnload) : lazyOnload chargeait
          GA4 APRÈS window.onload, qui prend 3-5s sur mobile Maroc 3G/4G (84% du
          trafic). Les sessions qui bouncent avant onload n'envoyaient pas de 1er
          page_view ou l'envoyaient sans landing page → ~83 sessions/sem en landing
          « (not set) » bounce 98.8% (gonflait le bounce global GA4). afterInteractive
          charge GA4 après hydration mais AVANT onload → capture les 1ers pageviews
          mobiles. Coût : 66KB JS async un peu plus tôt, mais CWV field reste GOOD
          (LCP 2.2s, INP 165ms — marge avant seuils 2.5s/200ms). Précision data >
          micro-gain perf. Cf bilan SEO 2026-05-17. */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          // Stub fbq — le tag "Google" GA4 a une intégration Meta/Facebook
          // liée côté admin (analytics.google.com) qui appelle fbq(...) en
          // interne, mais aucun Pixel Facebook n'est chargé sur ce site
          // (0 référence "fbq" dans le code) → ReferenceError non catché
          // à l'intérieur de gtag/js (Sentry SAHHA-1S/1Y). Shim standard
          // (identique au snippet officiel Meta base pixel) : queue les
          // appels sans planter, no-op tant qu'un vrai Pixel n'est pas
          // ajouté.
          window.fbq = window.fbq || function(){ (window.fbq.q = window.fbq.q || []).push(arguments); };
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            anonymize_ip: true,
            cookie_domain: '.sahha.ma',
            cookie_flags: 'SameSite=Lax;Secure',
            // send_page_view DESACTIVE — voir GA4PageTracker ci-dessous qui
            // envoie un page_view explicite avec params capturés au moment
            // de l'event (pas au moment du send). Sans ça : race condition
            // titre/path sur transition rapide (audit GA4 2026-06-15).
            send_page_view: false,
            linker: { domains: ${JSON.stringify(SAHHA_SUBDOMAINS)}, accept_incoming: true }
          });
        `}
      </Script>
    </>
  );
}

/**
 * GA4PageTracker — envoie un page_view explicite à chaque navigation client
 * (App Router) APRES un délai laissant React patcher document.title.
 *
 * Pattern Next.js officiel (App Router) :
 * https://nextjs.org/docs/app/building-your-application/optimizing/third-party-libraries
 *
 * Params capturés au moment de l'event (closure) → garantis cohérents :
 * - page_path : pathname + searchParams au moment du hit (pas window.location
 *   qui peut avoir changé entre temps)
 * - page_title : document.title après délai 100ms (App Router metadata est
 *   appliqué dans un effect, donc disponible après ce délai)
 * - page_location : window.location.href au moment du send
 *
 * Composant à monter UNE FOIS dans le layout après <GoogleAnalytics>.
 */
export function GA4PageTracker({ measurementId }: { measurementId: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined" || !window.gtag || !measurementId) return;
    const query = searchParams.toString();
    // SÉCURITÉ (audit 2026-09-10) : la query partait telle quelle chez Google.
    // Le lien de réinitialisation étant /mot-de-passe-reinit?token=<JWT>, le
    // jeton se retrouvait dans GA4. Cf. src/lib/analytics/sanitize-url.ts.
    const url = sanitizeAnalyticsUrl(pathname + (query ? `?${query}` : ""));
    // Délai 100ms : laisse Next.js App Router metadata (generateMetadata)
    // patcher document.title via son effect. Sans délai, le title capturé
    // est celui de la PAGE PRECEDENTE → (not set) ou wrong title GA4.
    const timer = window.setTimeout(() => {
      try {
        window.gtag?.("event", "page_view", {
          page_path: url,
          page_location: sanitizeAnalyticsUrl(window.location.href),
          page_title: document.title || url,
          send_to: measurementId,
          // transport_type beacon : assure que le hit part via
          // navigator.sendBeacon() meme si l'utilisateur ferme l'onglet
          // pendant les 100ms d'attente. Sans ca, le hit est perdu et
          // la session est comptee avec landing="(not set)" (audit GA4
          // 18/06 : 241 sessions/7j en (not set) malgre le fix initial
          // ac9773e25).
          transport_type: "beacon",
        });
      } catch {
        // Analytics ne doit jamais casser une app.
      }
    }, 100);
    return () => window.clearTimeout(timer);
  }, [pathname, searchParams, measurementId]);

  return null;
}
